import { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { AlertErrorCircle, FolderCheck, Retry } from '@/assets/icons'
import { MatterDialogType, MattersActions } from '@/matter/store/slice'
import { selectHasNoNonSampleMatters, selectSampleMatter } from '@/matter/store/selectors'
import { RootState } from '@/store/store'
import { Button } from '@/components/button/Button'
import { SampleMatterCard } from '@/components/sample-matter-card/SampleMatterCard'
import { useNavigate } from 'react-router-dom'
import { matterPaths } from '@/routing/paths'
import { kPaxtonName } from '@/constants/constants-strings'

interface MattersTableStatusViewProps {
  /**
   * Current search query
   */
  searchQuery: string

  /**
   * Whether there are no search results (has search query but no filtered rows)
   */
  hasNoSearchResults: boolean

  /**
   * Callback to clear the search query
   */
  onClearSearch: () => void
}

/**
 * MattersTableStatusView
 *
 * Renders a visual status message or loader when the table is loading, empty, has no search results, or has encountered an error.
 * This component replaces the table body during those states and is never shown alongside row data.
 */
export default function MattersTableStatusView({ searchQuery, hasNoSearchResults, onClearSearch }: MattersTableStatusViewProps) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const brandName = kPaxtonName

  // Redux state
  const isLoading = useAppSelector((state: RootState) => state.mattersState.loading)
  const hasError = useAppSelector((state: RootState) => state.mattersState.error)
  const initialLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)
  const hasNoNonSampleMatters = useAppSelector(selectHasNoNonSampleMatters)
  const sampleMatter = useAppSelector(selectSampleMatter)

  // Local flag to indicate that a retry is in progress - used to keep the spinner visible for at least `MIN_SPINNER_MS`
  const [retrying, setRetrying] = useState(false)
  const MIN_SPINNER_MS = 500

  /**
   * Reset the retrying flag after MIN_SPINNER_MS to ensure the spinner shows for a minimum duration.
   * UX Goal: Prevent the appearance of a quick flash when the API response returns very quickly.
   */
  useEffect(() => {
    if (retrying) {
      const timeout = setTimeout(() => {
        setRetrying(false)
      }, MIN_SPINNER_MS)

      return () => clearTimeout(timeout) // Cleanup timeout on unmount or when retrying changes
    }
  }, [retrying])

  // Handles retry clicks by forcing a matter list refresh
  const handleRetry = () => {
    // Prevent multiple simultaneous retries
    if (retrying || isLoading) return

    setRetrying(true)
    dispatch(MattersActions.refreshMatterList({ forceRefresh: true }))
  }

  const handleExploreSampleMatter = () => {
    if (!sampleMatter) return

    dispatch(MattersActions.setActiveMatterId({ matterId: sampleMatter.id }))
    navigate(matterPaths.overview(sampleMatter.id))
  }

  // Only the initial load (or a manual retry) shows the spinner. A background poll flips `loading`
  // every cycle, and must not flash a spinner over the empty or no-results view.
  if ((isLoading && !initialLoadComplete) || retrying) {
    return (
      <div className="flex items-center justify-center h-36">
        <CircularProgressContinuousSized size={24} thickness={5} />
      </div>
    )
  }

  // Likewise, once the list has loaded, a failed background poll shouldn't flash the error state.
  if (hasError && !initialLoadComplete) {
    return (
      <div className="flex flex-col gap-3.5 items-center justify-center h-36">
        {/* Error message */}
        <div className="flex flex-nowrap items-center gap-1 text-sm text-red-800 whitespace-nowrap">
          <AlertErrorCircle className="w-4 h-4" />
          <p>Failed to load matters</p>
        </div>
        {/* Retry button */}
        <Button onClick={handleRetry} trailingIcon={<Retry className="w-4 h-4" />} size="sm" variant="tonal" color="neutral" aria-label="Retry">
          Retry
        </Button>
      </div>
    )
  }

  if (hasNoSearchResults) {
    return (
      <div className="flex items-center justify-center h-[68px]">
        {/* Max width of 90% of container prevents horizontal scroll on small screens */}
        <div className="flex items-center gap-3 px-2 py-2 max-w-[90%]">
          {/* No results message */}
          <p className="text-xs sm:text-sm flex items-center min-w-0">
            {/* "Search" word hidden on mobile to save space */}
            <span className="shrink-0">
              No <span className="hidden sm:inline">Search </span>Results for "
            </span>
            {/* Only the query text truncates with ellipsis when too long */}
            <span className="truncate min-w-0">{searchQuery}</span>
            {/* Closing quote always visible */}
            <span className="shrink-0">"</span>
          </p>
          {/* Clear search button */}
          <Button onClick={onClearSearch} size="xs" variant="tonal" color="neutral" aria-label="Clear search">
            Clear
          </Button>
        </div>
      </div>
    )
  }

  if (hasNoNonSampleMatters) {
    return (
      <div className="bg-surface-content flex flex-col items-center justify-center gap-6 p-8">
        {sampleMatter && (
          <SampleMatterCard
            title={sampleMatter.name}
            description={sampleMatter.description ?? ''}
            tag="Sample Matter"
            headerBannerText={`Explore this pre-built sample case to see ${brandName} in action`}
            button={{ label: 'Explore', onClick: handleExploreSampleMatter }}
          />
        )}
        <div className="bg-surface-emphasis border border-border-default rounded-lg flex flex-col gap-4 items-center px-4 py-6 w-full">
          {/* Icon */}
          <FolderCheck className="w-[62.5px] h-10 text-text-tertiary" />

          {/* Text */}
          <div className="flex flex-col gap-0.5 items-center text-center max-w-[420px]">
            <h3 className="text-base font-medium text-black">Create your first matter</h3>
            <p className="text-sm text-text-tertiary">
              Matters keep your work case-specific. Organize files, research, and draft documents in one dedicated workspace per case.
            </p>
          </div>

          {/* Button */}
          <Button
            onClick={() =>
              dispatch(
                MattersActions.openDialog({
                  dialogType: MatterDialogType.Create,
                  navigateToMatterOnCreate: true,
                })
              )
            }
            size="sm"
            variant="filled"
            color="primary"
          >
            New Matter
          </Button>
        </div>
      </div>
    )
  }

  // If none of the above conditions are true, no need to render anything
  return null
}
